// Deep import on purpose: the shared prefix lives in a browser-safe, dependency-free leaf module,
// which keeps this file free of store/component imports (see header comment below).
import { CUSTOM_DATA_LAYER_ID_PREFIX, SITE_DATA_LAYER_ID_PREFIX } from '@civilgrid/utils/src/extract/constants';
import type { ProjectCustomData } from '~/api/projectCustomData';
import type { TOC, TOCItem, TOCParent } from '~/common/types';
import { getSiteDataLayerLabel, SITE_DATA_SECTION_LABEL } from './extract/siteDataLayers';

/**
 * The history stores the ESRI/FME layer names an export requested, not the picker's layer ids, so
 * naming a layer for the user means reversing that mapping and then finding the layer in the TOC.
 * Custom-data entries (`customData:<id>`) never map to a picker id — they resolve against the
 * project's custom-data rows instead.
 *
 * Kept free of runtime imports so it can be tested without loading the store or component tree.
 */

/** Shown for a customData: entry whose row no longer exists (deleted since the export ran). */
export const CUSTOM_DATA_LAYER_FALLBACK_LABEL = 'Custom layer';

// Legacy ESRI runs request the project outline alongside the user's layers; it is never a picker layer
export const LEGACY_PROJECT_OUTLINE_LAYER = 'Project Outline';

// Mirrors the extract picker's section label (extract/customLayers.ts) — kept as a local copy so
// this module stays free of the picker's runtime imports.
const CUSTOM_DATA_SECTION_LABEL = 'Custom Data';

/** A layer named for display, split so its TOC section can be de-emphasised against its own label. */
export type ResolvedExportLayer = {
  id: string;
  parent?: string;
  label: string;
};

export type TocLayerIndex = Map<string, { parent?: string; label: string }>;

function isTOCParent(item: TOCItem): item is TOCParent {
  return 'children' in item;
}

/** Flattens the TOC to layer id → its own label plus the section holding it. */
export function buildTocLayerIndex(sources: { toc: TOC; parent?: string }[]): TocLayerIndex {
  const index: TocLayerIndex = new Map();

  function walk(item: TOCItem, parent?: string) {
    if (isTOCParent(item)) {
      for (const child of item.children) walk(child, item.label);
    } else {
      index.set(item.id, { parent, label: item.label });
    }
  }

  for (const { toc, parent } of sources) {
    for (const item of toc) walk(item, parent);
  }

  return index;
}

/**
 * An extract submission records the picker's own layer ids, while legacy ESRI downloads record the
 * GPServer names, so a stored layer may already be an id or may need the mapping reversed. Overrides
 * win on the reverse lookup, since a layer that has one is requested under that name. `acceptBaseName`
 * also resolves an overridden layer from its base name, which some legacy rows store (`Parcels` for
 * `Parcels_Production`).
 */
export function toLayerId(
  storedLayer: string,
  layerIdToDownloadLayer: Record<string, string>,
  overrides: Record<string, string>,
  { acceptBaseName = false }: { acceptBaseName?: boolean } = {},
): string | undefined {
  if (layerIdToDownloadLayer[storedLayer]) return storedLayer;

  return Object.keys(layerIdToDownloadLayer).find((layerId) =>
    overrides[layerId]
      ? overrides[layerId] === storedLayer || (acceptBaseName && layerIdToDownloadLayer[layerId] === storedLayer)
      : layerIdToDownloadLayer[layerId] === storedLayer,
  );
}

/**
 * Names each requested layer for display. Layers that map to nothing in the picker are dropped —
 * the project outline is requested alongside the user's selection but was never one of them.
 */
export function resolveExportLayers(
  downloadLayerNames: string[],
  index: TocLayerIndex,
  maps: {
    layerIdToDownloadLayer: Record<string, string>;
    overrides: Record<string, string>;
    customDataById?: Record<string, ProjectCustomData>;
  },
): ResolvedExportLayer[] {
  const resolved: ResolvedExportLayer[] = [];

  for (const name of downloadLayerNames) {
    // Custom-data entries resolve against the project's rows and NEVER drop: dropping them made
    // the count lie about what the export contained (EXP-21 x CG-694 regression).
    if (name.startsWith(CUSTOM_DATA_LAYER_ID_PREFIX)) {
      const rowId = name.slice(CUSTOM_DATA_LAYER_ID_PREFIX.length);
      resolved.push({
        id: name,
        parent: CUSTOM_DATA_SECTION_LABEL,
        label: maps.customDataById?.[rowId]?.label ?? CUSTOM_DATA_LAYER_FALLBACK_LABEL,
      });
      continue;
    }

    // Site-data entries (`siteData:*`) resolve against the catalog — same never-drop rule.
    if (name.startsWith(SITE_DATA_LAYER_ID_PREFIX)) {
      resolved.push({ id: name, parent: SITE_DATA_SECTION_LABEL, label: getSiteDataLayerLabel(name) });
      continue;
    }

    const layerId = toLayerId(name, maps.layerIdToDownloadLayer, maps.overrides);

    if (!layerId) continue;

    const entry = index.get(layerId);
    const isDigitized = name.toLowerCase().includes('digitized');
    const label = entry?.label ?? name;

    resolved.push({
      id: layerId,
      parent: entry?.parent,
      label: isDigitized && !label.toLowerCase().includes('digitized') ? `${label} (Digitized)` : label,
    });
  }

  return resolved;
}
